// ============================================================
// VERIX — Lightweight error reporter
// Captures unhandled errors and provides window.errorTracker
// for integration with loggerService.
//
// Sprint 6 T7 — Sentry integration hook.
// Rather than importing @sentry/react (200kb bundle, unknown DSN), this
// module exposes a `registerExternalReporter(fn)` callback that Sentry's
// init can register. When registered, every captureException and
// captureMessage call is forwarded synchronously. Flip the switch by:
//
//   // In main.jsx after initErrorReporter():
//   import * as Sentry from '@sentry/react';
//   Sentry.init({ dsn: import.meta.env.VITE_SENTRY_DSN, tracesSampleRate: 0.1 });
//   registerExternalReporter((err, ctx) => Sentry.captureException(err, { extra: ctx }));
//
// Until then, the buffer + console fallback + window.errorTracker keep
// working for manual debug.
// ============================================================

interface ErrorEntry {
  message: string;
  stack?: string;
  timestamp: string;
  url: string;
  source?: string;
  filename?: string;
  lineno?: number;
  colno?: number;
  [key: string]: any;
}

type ExternalReporter = (error: Error, context?: Record<string, any>) => void;

const MAX_BUFFER = 50;
const errorBuffer: ErrorEntry[] = [];
let externalReporter: ExternalReporter | null = null;

/**
 * Sprint 6 T7 — plug Sentry (or any observability SDK) into the reporter.
 * Safe to call before or after initErrorReporter(); buffered events captured
 * prior to registration are flushed on register so nothing is lost.
 */
export function registerExternalReporter(reporter: ExternalReporter): void {
  externalReporter = reporter;
  // Flush buffered events so errors captured during cold start reach Sentry.
  for (const entry of errorBuffer) {
    const err = new Error(entry.message);
    err.stack = entry.stack;
    try {
      reporter(err, { ...entry, buffered: true });
    } catch {
      /* external reporter must not break the app */
    }
  }
}

function captureException(error: Error | unknown, context?: Record<string, any>): void {
  const err = error instanceof Error ? error : new Error(String(error));
  const entry: ErrorEntry = {
    message: err.message,
    stack: err.stack,
    timestamp: new Date().toISOString(),
    url: typeof location !== 'undefined' ? location.href : '',
    ...context,
  };
  errorBuffer.push(entry);
  if (errorBuffer.length > MAX_BUFFER) errorBuffer.shift();

  if (externalReporter) {
    try {
      externalReporter(err, context);
    } catch {
      /* swallow — reporter failures must not cascade */
    }
  }
}

function captureMessage(message: string, context?: Record<string, any>): void {
  captureException(new Error(message), context);
}

/**
 * captureLog — drop-in for legacy `console.error/warn/info` callsites.
 * Preserves dev console output (visible in browser DevTools) while routing
 * the same payload to the buffer + Sentry. Picks the first Error-typed arg
 * as the exception; falls back to a synthesized Error from joined string args.
 *
 * Usage:
 *   const __LOG_CTX = { context: 'src/components/services/Foo' };
 *   captureLog('error', __LOG_CTX, 'Failed to fetch X:', err);
 */
function captureLog(
  level: 'error' | 'warn' | 'info',
  ctx: Record<string, any>,
  ...args: unknown[]
): void {
  if (import.meta.env.DEV && typeof console !== 'undefined') {
    // eslint-disable-next-line no-console -- intentional dev-only passthrough
    console[level](...args);
  }
  const errArg = args.find((a) => a instanceof Error) as Error | undefined;
  const messageParts = args
    .filter((a) => a !== errArg)
    .map((a) => (typeof a === 'string' ? a : safeStringify(a)));
  const err = errArg ?? new Error(messageParts.join(' ') || `[${level}] (no message)`);
  captureException(err, { ...ctx, level, ...(messageParts.length ? { detail: messageParts.join(' ') } : {}) });
}

function safeStringify(value: unknown): string {
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function getBuffer(): ErrorEntry[] {
  return errorBuffer.slice();
}

export function initErrorReporter(): void {
  if (typeof window === 'undefined') return;

  (window as any).errorTracker = { captureException, captureMessage, getBuffer };

  window.addEventListener('error', (event) => {
    captureException(event.error || new Error(event.message), {
      source: 'window.onerror',
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    const error = event.reason instanceof Error
      ? event.reason
      : new Error(String(event.reason));
    captureException(error, { source: 'unhandledrejection' });
  });
}

export { captureException, captureMessage, captureLog, getBuffer };
export default { initErrorReporter, captureException, captureMessage, captureLog, getBuffer, registerExternalReporter };
